/**
 * SoT for all kiosk session-transition strings (Child-Psych C5, B4 R6).
 *
 * Every kiosk transition string (lock-now button, session-lock warning, drop
 * messaging, child single-surface toggle, /my-money privacy copy, restore
 * resume) imports from THIS module — no inline literals at the call sites. The
 * banned-copy guard test lives alongside.
 *
 * COPY RULES (non-negotiable):
 *  - Calm, ~2nd–3rd-grade reading level.
 *  - No urgency, shame, failure, abandonment, or "you were away/idle" framing.
 *  - Jar metaphor for money; "family screen" (never "lock"/"sign out") for drop.
 *
 * CHANGING `KIOSK_BANNED_COPY` REQUIRES CHILD-PSYCH SIGN-OFF. The banned list is
 * a child-emotional-safety contract, not a style preference — do not add to or
 * remove from it without a Child-Psychologist persona review on the PR.
 */
export const KIOSK_TRANSITION_COPY = {
  /** Shown only on a SUCCESSFUL checklist-draft restore after re-elevation. */
  resumeAfterRestore: 'Pick up where you left off',
  /** Visible label on the persistent "back to ambient" affordance. */
  lockNowButton: 'Family screen',
  /** Accessible name for the same affordance. */
  lockNowAriaLabel: 'Go to the family screen',
  /** Softened drop-to-ambient message (Child-Psych I1) — no "timed out"/"away". */
  droppingToAmbient: 'All done for now — back to the family screen',
  /** Session-lock pre-warning headline (matches the current SessionLockWarning). */
  warningTitle: 'Taking a break?',
  /** Session-lock pre-warning body (matches the current SessionLockWarning). */
  warningBody:
    "This screen will lock soon to keep your family's stuff safe. Tap anywhere or choose Stay here to keep going.",
  /** Accessible name for the session-lock warning dialog. */
  warningAriaLabel: 'Taking a break? This screen will lock soon.',
  /** "Keep going" affordance on the session-lock warning (sourced here so the
   *  banned-copy guard covers it — no inline literal in SessionLockWarning). */
  warningStayButton: 'Stay here',
  /** /my-money on a kiosk-dependent posture — jar metaphor, device-agnostic. */
  myMoneyPrivate: 'Your jars are private. Ask a grown-up to look together.',
  /** Child single-surface "exit" toggle — reveal all of the child's surfaces. */
  showAllExit: 'Show all my stuff',
  /** Paired collapsed-state label for the single-surface toggle. */
  justMyTasks: 'Just my tasks',

  /* #A2 privacy scrim (KioskPrivacyScrim) — the full-screen "tap to resume"
     cover an ELEVATED ADULT kiosk session shows after 60s of inactivity, so a
     passer-by at the shared wall can't read on-screen PII. Calm, no urgency —
     it is a privacy convenience, not a lock. */
  /** Headline on the privacy scrim. */
  privacyScrimTitle: 'Paused for privacy',
  /** Body line on the privacy scrim. */
  privacyScrimBody: 'Tap anywhere to pick up where you were.',
  /** Visible label on the scrim's resume control. */
  privacyScrimButton: 'Tap to resume',
  /** Accessible name for the scrim dialog. */
  privacyScrimAriaLabel: 'Paused for privacy. Tap to resume.',

  /* #A2 per-field mask (KioskAutoMask) — collapses a single PII field behind a
     reveal control on an elevated DEPENDENT (CHILD/TEEN) session, the interim
     control until cross-member PII is server-projected for kiosk dependents. */
  /** Prefix of the reveal control's accessible name; the generic field label
   *  (e.g. "calendar event") is appended at the call site. */
  fieldMaskRevealPrefix: 'Hidden for privacy:',
  /** Suffix of the reveal control's accessible name. */
  fieldMaskRevealSuffix: 'Activate to reveal.',
  /** One-shot status announcement when a field is masked. */
  fieldMaskStatus: 'Content hidden for privacy. Activate to reveal.',
} as const;

/**
 * Case-insensitive substrings that MUST NOT appear in any kiosk transition copy.
 * These framings induce shame/anxiety or imply failure/abandonment for a child
 * on a shared family wall. CHANGING THIS LIST REQUIRES CHILD-PSYCH SIGN-OFF.
 */
export const KIOSK_BANNED_COPY = [
  'session expired',
  "you've been away",
  'didn’t finish',
  "didn't finish",
  'timed out',
  'are you still there',
  'inactive',
  'idle',
  'abandoned',
  'forgot',
  'gave up',
  'incomplete',
  'not finished',
  'failed',
  'failure',
  'missed',
  'come back',
  'where did you go',
  'lost progress',
  'will be lost',
  'streak broken',
] as const;
